import React, { useState, useEffect } from "react";
import { formatCurrency } from "../../../utils/format";
import "../../../styles/Budget.css";
import Toast from "../../../utils/Toast";
import AuthHandler from "../../../utils/AuthHandler";
import Axios from "../../../utils/Axios";
import { useNavigate } from "react-router-dom";
import { BUDGET_STATUS, DELETE_BUDGET } from "../../../config/API_Collection";
import { useSelector } from "react-redux";
import ActionModal from "../ActionModal";
import EditBudget from "./EditBudget";
import AddBudget from "./AddBudget";
import ActionDropDown from "./ActionDropDown";

function BudgetList({ data, refreshProjectData }) {
  const [toggles, setToggles] = useState({});
  const [toast, setToast] = useState({ message: "", type: "" });

  const navigate = useNavigate();

  const budgets = data?.budget_data || {};

  const [showModalAdd, setShowModalAdd] = useState(false);
  const OpenModalAdd = () => setShowModalAdd(true);
  const CloseModalAdd = () => setShowModalAdd(false);

  const [showModalEdit, setShowModalEdit] = useState(false);

  const [showModalRemove, setShowModalRemove] = useState(false);
  const [selectedBudgetIdRemove, setSelectedBudgetIdRemove] = useState(null);

  const login = useSelector((s) => s?.login);
  const actions = login?.actions || null;
  const canEditBudget = actions?.budgets?.edit_budget;
  const canListBudget = actions?.budgets?.budget_list;
  const canAddBudget = actions?.budgets?.new_budget;
  const canDeleteBudget = actions?.budgets?.delete_budget;

  const [editData, setEditData] = useState({
    budget_id: "",
    budget_name: "",
    budget_type: "",
    allocated_amount: "",
  });

  // NEW: per-form submit locks

  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);

  const OpenModalRemove = (bid) => {
    setSelectedBudgetIdRemove(bid);
    setShowModalRemove(true);
  };

  const OpenModalEdit = (bid, budget_name, budget_type, allocated_amount) => {
    setEditData({
      budget_id: bid,
      budget_name: budget_name || "",
      budget_type: budget_type || "",
      allocated_amount: allocated_amount || "",
    });
    setShowModalEdit(true);
  };
  const CloseModalRemove = () => setShowModalRemove(false);
  const CloseModalEdit = () => setShowModalEdit(false);

  useEffect(() => {
    const initialToggles = {};
    budgets.forEach((budget) => {
      initialToggles[budget.bid] = budget.status === 1;
    });
    setToggles(initialToggles);
  }, [budgets]);

  const handleToggle = async (bid) => {
    setToggles((prev) => ({
      ...prev,
      [bid]: !prev[bid],
    }));
    const newStatus = toggles[bid] ? 0 : 1;

    try {
      const payload = { budget_id: bid, status: newStatus };
      const response = await Axios.post(BUDGET_STATUS, payload);
      if (response.status === 200) {
        showToast("Budget Status Updated!", "success");
      }
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
    } finally {
      refreshProjectData();
    }
  };

  const showToast = (message, type) => setToast({ message, type });

  const handleRemoveBudget = async () => {
    if (isSubmittingDelete) return;
    setIsSubmittingDelete(true);

    try {
      const payload = { budget_id: selectedBudgetIdRemove };
      const response = await Axios.post(DELETE_BUDGET, payload);
      if (response.status === 200) {
        showToast("Budget Deleted Successfully!", "success");
        CloseModalRemove();
        refreshProjectData();
        return; // keep disabled; modal is closing
      }
      showToast("Failed to delete budget", "failed");
      setIsSubmittingDelete(false); // re-enable on non-200
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete budget",
        "failed"
      );
      setIsSubmittingDelete(false); // re-enable on error
    }
  };

  return (
    <div className="fs-6">
      <Toast message={toast.message} type={toast.type} />
      <div className="row">
        <div className="col-12 col-md mb-4">
          <span className="budget-summery">Budget Breakdown</span>
          <div className="card budget-card">
            <div className="card-body">
              <table className="table custom-table">
                <thead>
                  <tr>
                    <th className="">Active</th>
                    <th className="">Budget ID</th>
                    <th className="w-50">Budget Name</th>
                    <th className="">Type</th>
                    <th className="text-end">Budget Amount</th>
                    {(canEditBudget || canDeleteBudget) && (
                      <th className="">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {budgets.length > 0 && canListBudget ? (
                    budgets.map((budget) => (
                      <tr key={budget.bid}>
                        <td className="text-start">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input custom-toggle toggle-green"
                              type="checkbox"
                              id={`budget_active_${budget.bid}`}
                              checked={toggles[budget.bid] ?? false}
                              onChange={() => handleToggle(budget.bid)}
                            />
                          </div>
                        </td>
                        <td className="text-start">
                          BD{String(budget.bid).padStart(3, "0")}
                        </td>
                        <td className="text-start">{budget.budget_name}</td>
                        <td className="text-start">{budget.budget_type}</td>
                        <td className="text-end">
                          {formatCurrency(budget.budget)}
                        </td>
                        {(canEditBudget || canDeleteBudget) && (
                          <td className="text-end">
                            <ActionDropDown
                              showEdit={canEditBudget}
                              showDelete={canDeleteBudget}
                              onClickEdit={() =>
                                OpenModalEdit(
                                  budget.bid,
                                  budget.budget_name,
                                  budget.budget_type,
                                  budget.budget
                                )
                              }
                              onClickDelete={() => OpenModalRemove(budget.bid)}
                            />
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="text-center" colSpan={6}>
                        <div className="no-content-msg">
                          <span>No Budgets Available</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={6}>
                      <div className="d-flex mb-12">
                        {canAddBudget && (
                          <button
                            className="btn btn-outline-primary btn-sm d-flex align-items-center"
                            onClick={OpenModalAdd}
                          >
                            <i className="bi bi-plus-circle"></i>&nbsp; Add New
                            Budget
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="text-start ">
                      <span className="budget-total">Total Budget Amount</span>
                    </td>
                    <td colSpan={2} className="text-end">
                      <span className="budget-total">
                        {formatCurrency(data?.stat?.budget?.total)}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Budget Modal */}
      <ActionModal
        show={showModalAdd}
        title="Create New Budget"
        size="lg"
        modalBody={
          <AddBudget
            data={data}
            showToast={showToast}
            CloseModalAdd={CloseModalAdd}
            refreshProjectData={refreshProjectData}
            setToast={setToast}
            canAddBudget={canAddBudget}
          />
        }
      />

      {/* Edit Budget Modal */}
      <ActionModal
        show={showModalEdit}
        title="Update Budget"
        size="lg"
        modalBody={
          <EditBudget
            editData={editData}
            showToast={showToast}
            CloseModalEdit={CloseModalEdit}
            refreshProjectData={refreshProjectData}
            setToast={setToast}
          />
        }
      />

      {/* Delete Budget */}
      <ActionModal
        show={showModalRemove}
        title="Confirm Budget Deletion"
        confirmText="Yes"
        cancelText="No"
        isSubmitting={isSubmittingDelete}
        onConfirm={handleRemoveBudget}
        onCancel={CloseModalRemove}
        modalBody={
          <p className="text-center">
            Are you sure you want to delete this budget?
          </p>
        }
      />
    </div>
  );
}

export default BudgetList;
